import React from 'react';
import ValidationErrors from '../ProductView/ValidationErrors/ValidationErrors';
import type { RestrictionSet } from './RestrictionSet';
import { useTranslation } from 'react-i18next';
import { Accordion } from 'react-bootstrap';
import './RestrictionsModal.scss';
import TriStateCheckbox from '../../common/TriStateCheckbox/TriStateCheckbox';
import { RestrictionSchemeType } from '../../../data/models/territory';
import CancelChangesModal from '../../common/CancelChangesModal/cancelChangesModal';
import { useRestrictionsModal } from '@/hooks/useRestrictionsModal';
import type { IChangeRequest } from '@/data/models/changeRequest';
import type { ICurrentTerritoriesChangesGroup } from '@/types/product-current-changes-types';
import ChangeRequestIndicator from '@/components/common/ChangeRequestIndicator/ChangeRequestIndicator';
import DismissibleErrorAlert from '@/components/common/ErrorAlert/DismissibleErrorAlert';

interface RestrictionsModalProps {
  toggleRestrictionsModal: any;
  territoryRestrictionSet: RestrictionSet;
  readonly: boolean;
  updateProduct: (
    restrictions: string[],
    restrictionScheme: RestrictionSchemeType
  ) => Promise<void>;
  changeRequest?: IChangeRequest | undefined;
  currentChanges?: ICurrentTerritoriesChangesGroup;
}

export default function RestrictionsModal({
  toggleRestrictionsModal,
  territoryRestrictionSet,
  readonly,
  updateProduct,
  changeRequest,
  currentChanges,
}: RestrictionsModalProps) {
  const {
    restrictionSet,
    showPopUp,
    errorMessages,
    warningMessages,
    errorBanner,
    saveErrorMessage,
    setSaveErrorMessage,
    showCancelChangesModal,
    setShowCancelChangesModal,
    isSubmitting,
    closePopUp,
    handleSubmit,
    onCancel,
    onRegionUpdated,
    onTerritoryUpdated,
    worldwideClicked,
    allowOnlyClicked,
    denyOnlyClicked,
    isWorldwide,
    isAllowOnly,
    isDenyOnly,
    getButtonClass,
    getTerritoryIndicatorClass,
    hasRestrictionSchemeChange,
    hasRegionRestrictionChange,
    hasTerritoryRestrictionChange,
  } = useRestrictionsModal(
    territoryRestrictionSet,
    toggleRestrictionsModal,
    updateProduct,
    currentChanges,
    !!changeRequest
  );
  const { t } = useTranslation();

  const getRestrictionSchemeName = (scheme?: unknown) => {
    const restrictionScheme = String(scheme ?? '');
    const originalRestrictions = currentChanges?.territoryRestrictions.value;
    const hasOriginalRestrictions =
      Array.isArray(originalRestrictions) && originalRestrictions.length > 0;

    if (
      (restrictionScheme === 'DenyOnly' ||
        restrictionScheme === RestrictionSchemeType.DenyOnly.toString()) &&
      !hasOriginalRestrictions
    ) {
      return t('product.restrictions.worldwide');
    }

    if (
      restrictionScheme === 'AllowOnly' ||
      restrictionScheme === RestrictionSchemeType.AllowOnly.toString()
    ) {
      return t('product.restrictions.allowOnly');
    }

    if (
      restrictionScheme === 'DenyOnly' ||
      restrictionScheme === RestrictionSchemeType.DenyOnly.toString()
    ) {
      return t('product.restrictions.denyOnly');
    }

    return null;
  };

  return (
    <>
      <CancelChangesModal
        onSubmit={() => handleSubmit(true)}
        onClose={closePopUp}
        onCancel={() => setShowCancelChangesModal(false)}
        show={showCancelChangesModal}
        titleText={t('product.restrictions.distributionTerritories')}
        bodyText={t('product.restrictions.cancelChangesBody')}
      />
      {showPopUp ? (
        <div id="restrictionsModal" className="product-edit-mode-modal">
          <div className="product-edit-mode-modal-content">
            <div className="product-edit-mode-modal-header">
              <button
                className="btn-close"
                type="button"
                aria-label="Close"
                onClick={onCancel}
              >
                <i className="icon fas fa-times fa-lg" aria-hidden="true"></i>
              </button>
              <h2>{t('product.restrictions.distributionTerritories')}</h2>
            </div>

            <div className="product-edit-mode-modal-body">
              <div className="scroll-body">
                <DismissibleErrorAlert
                  error={saveErrorMessage}
                  onClose={() => setSaveErrorMessage(null)}
                />
                <ValidationErrors
                  errorBanner={errorBanner}
                  errors={errorMessages}
                  warningMessages={warningMessages}
                  region={'Edit'}
                ></ValidationErrors>

                <div className="restriction-settings">
                  <span>{t('product.restrictions.modalTooltip')}</span>
                  <div className="restriction-list-actions">
                    <div className="center btn-group">
                      <ChangeRequestIndicator
                        show={hasRestrictionSchemeChange}
                        customClass={
                          'restriction-list-actions-change-indicator'
                        }
                        displayValue={getRestrictionSchemeName(
                          currentChanges?.territoryRestrictionScheme.value
                        )}
                      ></ChangeRequestIndicator>
                      <button
                        id={'worldwide-button'}
                        type="button"
                        className={getButtonClass(isWorldwide(), readonly)}
                        onClick={worldwideClicked}
                        disabled={readonly}
                      >
                        <span>{t('product.restrictions.worldwide')}</span>
                      </button>
                      <button
                        type="button"
                        className={getButtonClass(isAllowOnly(), readonly)}
                        onClick={allowOnlyClicked}
                        disabled={readonly}
                      >
                        <span>{t('product.restrictions.allowOnly')}</span>
                      </button>
                      <button
                        type="button"
                        className={getButtonClass(isDenyOnly(), readonly)}
                        onClick={denyOnlyClicked}
                        disabled={readonly}
                      >
                        <span>{t('product.restrictions.denyOnly')}</span>
                      </button>
                    </div>
                  </div>
                  {!isWorldwide() ? (
                    <Accordion>
                      {restrictionSet.groups.map((group, index) => {
                        return (
                          <Accordion.Item
                            eventKey={index.toString()}
                            key={index}
                          >
                            <Accordion.Header>
                              <div className="restriction-group">
                                <span className="accordion-title">
                                  <TriStateCheckbox
                                    showX={restrictionSet.isDenyOnly}
                                    checked={restrictionSet.allSelected(group)}
                                    indeterminate={restrictionSet.someSelected(
                                      group
                                    )}
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      onRegionUpdated(group);
                                    }}
                                    checkboxLabel={t(
                                      `territory.region.${group}`
                                    )}
                                    disabled={readonly}
                                  ></TriStateCheckbox>
                                </span>
                                <div className="float-end">
                                  <ChangeRequestIndicator
                                    show={hasRegionRestrictionChange(group)}
                                    customClass={'restriction-territory-ci'}
                                    omitDisplayValue={true}
                                  ></ChangeRequestIndicator>
                                  <i className="icon-down fas fa-chevron-down"></i>
                                </div>
                              </div>
                            </Accordion.Header>
                            <Accordion.Body>
                              <ul className="restrictions-list">
                                {restrictionSet
                                  .getAllInGroup(group)
                                  .map((restriction) => {
                                    return (
                                      <li
                                        key={restriction.Restriction.Reference}
                                      >
                                        <TriStateCheckbox
                                          showX={restrictionSet.isDenyOnly}
                                          checked={restriction.Selected}
                                          checkboxLabel={
                                            restriction.Restriction.DisplayName
                                          }
                                          onClick={() =>
                                            onTerritoryUpdated(restriction)
                                          }
                                          disabled={readonly}
                                        ></TriStateCheckbox>
                                        <div className="float-end">
                                          {restriction.Selected ? (
                                            restrictionSet.isDenyOnly ? (
                                              <span className="badge badge-error">
                                                {t(
                                                  'product.restrictions.denied'
                                                )}
                                              </span>
                                            ) : (
                                              <span className="badge badge-ok">
                                                {t(
                                                  'product.restrictions.allowed'
                                                )}
                                              </span>
                                            )
                                          ) : null}
                                          <ChangeRequestIndicator
                                            show={hasTerritoryRestrictionChange(
                                              restriction
                                            )}
                                            customClass={getTerritoryIndicatorClass(
                                              restriction.Selected
                                            )}
                                            omitDisplayValue={true}
                                          ></ChangeRequestIndicator>
                                        </div>
                                      </li>
                                    );
                                  })}
                              </ul>
                            </Accordion.Body>
                          </Accordion.Item>
                        );
                      })}
                    </Accordion>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div className="edit-modal-spacer"></div>
              <div className="modal-footer-buttons">
                {!readonly ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-primary alt"
                      onClick={onCancel}
                      disabled={isSubmitting}
                    >
                      {t('common.button.cancel')}
                    </button>
                    <button
                      type="button"
                      value="Submit"
                      className="btn btn-primary buttonSave"
                      onClick={() => handleSubmit(true)}
                      disabled={isSubmitting}
                    >
                      {t('common.button.save')}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closePopUp}
                  >
                    {t('common.button.close')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
